import React, { useState, useEffect } from "react";
import SEO from "../seo";
const CreateAccountLandingPage = () => {
  const [activeSection, setActiveSection] = useState("sign-up");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "sign-up",
        "create-landing-page",
        "default-domain",
        "quick-steps",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Create Your Account and Landing Page – xPAGE Help Center"
        description="Learn how to create your xPage account and landing page. Step-by-step guide for signing up, setting up your default domain, and launching your first store page."
        keywords="xPAGE, create account, landing page setup, xPage domain, online store builder, eCommerce setup, xPage help"
        canonical="https://help.xpage.ai/help/landing-page-setup/Create%20Your%20Account%20and%20Landing%20Page"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          Create Your Account and Landing Page
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          Get started with xPage in just a few minutes by creating your account
          and setting up your landing page.
        </p>

        {/* Sign Up Section */}
        <section id="sign-up" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Sign Up for a xPage Account
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              Visit xPage.ai and click Start Free Trial. Enter a valid email
              address and create a secure password. Once your account is
              created, you'll be guided directly into the Landing Page Setup
              process.
            </p>
          </div>
        </section>

        {/* Create Landing Page Section */}
        <section id="create-landing-page" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Create Your Landing Page
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-system600 mb-3">
              xPage guides you step-by-step to launch your landing page. You
              can:
            </p>

            {/* Product Creation Methods */}
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-systemTitle mb-2">
                Product Creation Methods (Physiqal Products)
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="border border-systemBorder rounded-lg p-3 text-center">
                  <i className="fas fa-link text-2xl text-core mb-2"></i>
                  <h4 className="text-lg font-semibold text-systemTitle mb-1">
                    Import via Link
                  </h4>
                  <p className="text-sm text-system600">
                    From AliExpress, Amazon, Shopify
                  </p>
                </div>
                <div className="border border-systemBorder rounded-lg p-3 text-center">
                  <i className="fas fa-edit text-2xl text-core mb-2"></i>
                  <h4 className="text-lg font-semibold text-systemTitle mb-1">
                    Create Manually
                  </h4>
                  <p className="text-sm text-system600">
                    Build products from scratch
                  </p>
                </div>
                <div className="border border-systemBorder rounded-lg p-3 text-center">
                  <i className="fas fa-image text-2xl text-core mb-2"></i>
                  <h4 className="text-lg font-semibold text-systemTitle mb-1">
                    Image & Prompt
                  </h4>
                  <p className="text-sm text-system600">
                    Create products using images and Prompt
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <h3 className="text-sm font-semibold text-systemTitle mb-2">
                Product Creation Methods (Digital Products)
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="border border-systemBorder rounded-lg p-3 text-center">
                  <i className="fas fa-link text-2xl text-core mb-2"></i>
                  <h4 className="text-lg font-semibold text-systemTitle mb-1">
                    Import via Link
                  </h4>
                  <p className="text-sm text-system600">
                    From AliExpress, Amazon, Shopify and Upload Your file inside
                    of your product detail page.
                  </p>
                </div>
                <div className="border border-systemBorder rounded-lg p-3 text-center">
                  <i className="fas fa-edit text-2xl text-core mb-2"></i>
                  <h4 className="text-lg font-semibold text-systemTitle mb-1">
                    Create Manually
                  </h4>
                  <p className="text-sm text-system600">
                    Build products from scratch and Upload Your file inside of
                    your product detail page.
                  </p>
                </div>
                <div className="border border-systemBorder rounded-lg p-3 text-center">
                  <i className="fas fa-image text-2xl text-core mb-2"></i>
                  <h4 className="text-lg font-semibold text-systemTitle mb-1">
                    Image & Prompt
                  </h4>
                  <p className="text-sm text-system600">
                    Create products using images and Prompt and Upload Your file
                    inside of your product detail page.
                  </p>
                </div>
              </div>
            </div>

            {/* Setup Details */}
            <div>
              <h3 className="text-sm font-semibold text-systemTitle mb-2">
                Required Setup Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                <div className="flex items-center p-3 border border-systemBorder rounded-lg">
                  <i className="fas fa-signature text-core mr-3"></i>
                  <span className="text-sm text-system600">
                    Landing page name
                  </span>
                </div>
                <div className="flex items-center p-3 border border-systemBorder rounded-lg">
                  <i className="fas fa-envelope text-core mr-3"></i>
                  <span className="text-sm text-system600">Contact email</span>
                </div>
                <div className="flex items-center p-3 border border-systemBorder rounded-lg">
                  <i className="fas fa-phone text-core mr-3"></i>
                  <span className="text-sm text-system600">Phone number</span>
                </div>
                <div className="flex items-center p-3 border border-systemBorder rounded-lg">
                  <i className="fas fa-map-marker-alt text-core mr-3"></i>
                  <span className="text-sm text-system600">
                    Business address
                  </span>
                </div>
                <div className="flex items-center p-3 border border-systemBorder rounded-lg">
                  <i className="fas fa-clock text-core mr-3"></i>
                  <span className="text-sm text-system600">Time zone</span>
                </div>
                <div className="flex items-center p-3 border border-systemBorder rounded-lg">
                  <i className="fas fa-dollar-sign text-core mr-3"></i>
                  <span className="text-sm text-system600">
                    Default currency
                  </span>
                </div>
                <div className="flex items-center p-3 border border-systemBorder rounded-lg">
                  <i className="fas fa-weight text-core mr-3"></i>
                  <span className="text-sm text-system600">Weight unit</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Templates Section */}
        <section id="templates" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Using Landing Pages as Templates
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              You can turn any landing page into a reusable template, allowing
              you to quickly generate new pages while keeping the same layout
              and color system. This is especially useful when creating pages
              for different products or multiple languages while maintaining
              design consistency.
            </p>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              How to Set a Landing Page as a Template
            </h3>

            <p className="text-sm text-systemLabel mb-3">
              Only published landing pages (not drafts) can be used as
              templates. You can mark a page as a template in two ways:
            </p>

            <ul className="list-disc list-inside text-sm text-systemLabel space-y-2 mb-4">
              <li>
                <span className="font-bold">From the Builder:</span> Open your
                landing page and <strong>click the ⭐ (star icon)</strong>  to mark it as a
                template.
              </li>
              <li>
                <span className="font-bold">
                  From the Landing Pages Dashboard:
                </span>{" "}
                Click the <strong>three dots (⋯)</strong> next to a published landing page and
                select <strong>“Mark as template.”</strong>
              </li>
            </ul>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              How Templates Work
            </h3>

            <ul className="list-disc list-inside text-sm text-systemLabel space-y-2">
              <li>
                Once a landing page is set as a template, it becomes available
                during the generation process.
              </li>
              <li>
                When generating a new page from a product or link, you can
                select your template.
              </li>
              <li>The layout and color system will remain the same.</li>
              <li>
                The content and language will automatically adapt based on your
                selected preferences and product details.
              </li>
            </ul>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-4">
              <p className="text-sm text-systemLabel">
                This allows you to scale faster while keeping a consistent
                visual identity across all your landing pages.
              </p>
            </div>
          </div>
        </section>
        {/* Default Domain Section */}
        <section id="default-domain" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Default Domain
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              xPage automatically assigns a default domain in the format
              yourlandingpage.xpage.ai. This domain is used for logging into
              your admin, verifying your account, and connecting third-party
              services. You can keep this domain for customer access or connect
              a custom domain anytime.
            </p>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tip:</span> The default xpage.ai
                domain can only be set once.
              </p>
            </div>
          </div>
        </section>

        {/* Quick Steps Section */}
        <section id="quick-steps" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Quick Steps to Create Your Account and Landing Page
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel">
              <li className="mb-2">
                <span className="font-medium">
                  Go to <strong>xPage.ai</strong> and click{" "}
                  <strong>Start Free Trial</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Enter your email and password, then click{" "}
                  <strong>Sign Up</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Choose <strong>Import Product</strong> or{" "}
                  <strong>Create Blank Landing Page</strong>
                </span>
                <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-2">
                  <p className="text-sm text-systemLabel">
                    <span className="font-medium">Note:</span> To generate a
                    landing page directly without adding products first, only
                    "Generate LP via product link" is available
                  </p>
                </div>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Fill in your landing page details
                </span>{" "}
                (name, contact info, address, time zone, currency, weight unit)
                to set your brand identity
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Review your default <strong>xpage.ai</strong> domain
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click <strong>Create Landing Page</strong> to complete setup
                </span>
              </li>
            </ol>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#sign-up"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "sign-up"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Sign Up
              </a>
            </li>
            <li>
              <a
                href="#create-landing-page"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "create-landing-page"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Create Landing Page
              </a>
            </li>
            <li>
              <a
                href="#default-domain"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "default-domain"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Default Domain
              </a>
            </li>
            <li>
              <a
                href="#quick-steps"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "quick-steps"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Quick Steps
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CreateAccountLandingPage;
